import { logTransaction } from '../../common/log';
import { encodeInvalidCharacters, handleApiError, systemPathToBase64Url } from '../../common/util';
import { AttachmentAction, AttachmentAssetType, IAttachment, IUpdateMediaAttachments } from '../../models/dms/attachment.model';
import { IMediaSettings } from '../../models/dms/systemConfiguration.model';
import { LogType } from '../../models/log.model';
import axiosDmsInstance from './axiosDmsInstance';

export const getMediaSettings = async () => {
  try {
    const url = `/api/common/mediaattachment/v1/settings/`;
    let params = {} as any;

    const response = await axiosDmsInstance.get<IMediaSettings>(`${url}`, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getMediaSettings', err);
  }
};

export const getMediaAttachment = async (type: AttachmentAssetType, key: string, lineNumber: number) => {
  try {
    const url = `/api/common/mediaattachment/v1/attachment/${type}/${encodeInvalidCharacters(key)}/${lineNumber}`;
    let params = {} as any;

    const response = await axiosDmsInstance.get<Blob>(`${url}`, {
      params,
      responseType: 'blob',
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getMediaAttachment', err);
  }
};

export const getMediaAttachmentThumbnail = async (type: AttachmentAssetType, key: string, lineNumber: number, width: number, height: number) => {
  try {
    const url = `/api/common/mediaattachment/v1/attachment/${type}/${encodeInvalidCharacters(key)}/${lineNumber}/thumbnail/${width}/${height}`;
    let params = {} as any;

    const response = await axiosDmsInstance.get<Blob>(`${url}`, {
      params,
      responseType: 'blob',
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('getMediaAttachment', err);
  }
};

export const putMediaAttachments = async (type: AttachmentAssetType, key: string, attachments: IAttachment[], repairOrderNumber?: string, jobCode?: string, jobType?: string, mediaRef?: string, fileNumber?: string) => {
  try {
    logTransaction(LogType.Info, `Updating attachments for key: ${key}, repairOrderNumber: ${repairOrderNumber}, jobCode: ${jobCode}, jobType: ${jobType}`);
    const newAttachments = [] as IAttachment[];
    logTransaction(LogType.Info, `Attachments: ${attachments.length}`);
    for (const attachment of attachments) {
      logTransaction(LogType.Info, `Attachment: ${attachment.lineNumber}`);
      if (attachment.action === AttachmentAction.Add) {
        logTransaction(LogType.Info, `Attachment is new`);
        let newAttachment = {
          fileName: attachment.fileName,
          key: key,
          lineNumber: 0,
          description: undefined,
          originalFileName: undefined,
          refType: 'RO',
          refNumber: repairOrderNumber,
          mediaType: attachment.mediaType,
          action: AttachmentAction.Add,
        } as IAttachment;
        // Attachment is a new attachment. Extract the necessary data
        if (attachment.base64Url) {
          logTransaction(LogType.Info, `Attachment has base64Url: ${attachment.base64Url.substring(0, 40)}`);
          // Attachment is likely from web
          newAttachments.push({
            ...newAttachment,
            base64Url: attachment.base64Url,
          } as IAttachment);
        } else if (attachment.path) {
          // Attachment is from native, caputure the base64 data
          logTransaction(LogType.Info, `Attachment has path`);
          const base64Url = await systemPathToBase64Url(attachment.path, attachment.mimeType || 'image/png');
          if (base64Url) {
            logTransaction(LogType.Info, `Attachment is now base64Url: ${base64Url.substring(0, 40)}`);
            newAttachments.push({
              ...newAttachment,
              base64Url: base64Url,
            } as IAttachment);
          } else {
            logTransaction(LogType.Error, `Attachment has no base64Url`);
          }
        } else {
          logTransaction(LogType.Error, `Attachment has no base64Url or path`);
        }
      } else {
        logTransaction(LogType.Info, `Attachment is not new`);
        newAttachments.push({ ...attachment });
      }
    }

    logTransaction(LogType.Info, `PUT to API: ${newAttachments.length} attachments`);
    const url = `/api/common/mediaattachment/v1/attachment/${type}`;
    let params = {} as any;
    const body = {
      key,
      refType: 'RO',
      refNo: repairOrderNumber?.toString(),
      info1: [jobCode && `[[jcode=${jobCode}]]`, jobType && `[[jtype=${jobType}]]`, mediaRef && `[[ref=${mediaRef}]]`, fileNumber && `[[preq=${fileNumber}]]`].filter(Boolean).join(''),
      attachments: newAttachments,
    } as IUpdateMediaAttachments;

    const response = await axiosDmsInstance.put<IUpdateMediaAttachments>(`${url}`, body, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('putWorkshopVehicleRoAttachments', err);
  }
};
